import SequenceConfigStep from '../steps/SequenceConfigStep';
import AssetSelectionStep from '../steps/AssetSelectionStep';
import { WizardFlowResponse } from './WizardFlowResponse';
import ReviewStep from '../steps/ReviewStep';
import AssignmentStep from '../steps/AssignmentStep';
import SubmitStep from '../steps/SubmitStep';
import ScheduleStep from '../steps/ScheduleStep';

/**
 * @typedef {import('./../steps/BaseStep').default} BaseStep
 */

/**
 * Registry of all available step definitions keyed by step ID.
 * @type {Object.<string, {id: string, title: string, component: typeof BaseStep}>}
 */
const allStepDefinitions = {
  [SequenceConfigStep.id]: {
    id: SequenceConfigStep.id,
    title: SequenceConfigStep.title,
    component: SequenceConfigStep,
  },
  [AssetSelectionStep.id]: {
    id: AssetSelectionStep.id,
    title: AssetSelectionStep.title,
    component: AssetSelectionStep,
  },
  [ReviewStep.id]: {
    id: ReviewStep.id,
    title: ReviewStep.title,
    component: ReviewStep,
  },
  [AssignmentStep.id]: {
    id: AssignmentStep.id,
    title: AssignmentStep.title,
    component: AssignmentStep,
  },
  [SubmitStep.id]: {
    id: SubmitStep.id,
    title: SubmitStep.title,
    component: SubmitStep,
  },
  [ScheduleStep.id]: {
    id: ScheduleStep.id,
    title: ScheduleStep.title,
    component: ScheduleStep,
  },
};
/**
 * Ordered array of step definitions for the wizard flow.
 * @type {Array<{id: string, title: string, component: typeof BaseStep}>}
 */
const stepOrder = [
  allStepDefinitions[SequenceConfigStep.id],
  allStepDefinitions[AssetSelectionStep.id],
  allStepDefinitions[ReviewStep.id],
  // allStepDefinitions[ScheduleStep.id], disabled by request from David
  allStepDefinitions[AssignmentStep.id],
  allStepDefinitions[SubmitStep.id],
];

/**
 * Manages the flow of steps in the wizard.
 */
export default class WizardFlow {
  /**
   * Gets the step definition by its index in the flow.
   * @param {number} flowIndex - The index of the step in the flow.
   * @returns {WizardFlowResponse} The step definition wrapped in a WizardFlowResponse.
   */
  static getStepByIndex(flowIndex) {
    return new WizardFlowResponse(
      stepOrder[flowIndex]?.component || null,
      flowIndex,
      0,
      stepOrder.length - 1,
    );
  }

  /**
   * Gets the previous step in the flow relative to the current index.
   * @param {number} currentIndex - The current step index.
   * @returns {WizardFlowResponse} The previous step definition wrapped in a WizardFlowResponse.
   */
  static getPreviousStep(currentIndex) {
    const previousStep = WizardFlow.getStepByIndex(currentIndex - 1);

    return previousStep.isFound ? previousStep : WizardFlow.getStepByIndex(currentIndex);
  }

  /**
   * Gets the next step in the flow relative to the current index.
   * @param {number} currentIndex - The current step index.
   * @returns {WizardFlowResponse} The next step definition wrapped in a WizardFlowResponse.
   */
  static getNextStep(currentIndex) {
    const nextStep = WizardFlow.getStepByIndex(currentIndex + 1);

    return nextStep.isFound ? nextStep : WizardFlow.getStepByIndex(currentIndex);
  }
}
